/** Megaventory web UI document links (inventory/default.aspx). */

const MEGAVENTORY_ACCOUNT_WEB_ORIGIN = "https://smartgridsolutions.megaventory.com";

/**
 * Product editor. Do not use `document=Product` — that route is undefined in Megaventory.
 * Match: `/inventory/default.aspx?data=editProduct&id=<ProductID>&name=<SKU>`.
 */
export function megaventoryProductEditUrl(productMvId: number, sku: string | null): string {
  const name = encodeURIComponent((sku ?? "").trim() || String(productMvId));
  return `${MEGAVENTORY_ACCOUNT_WEB_ORIGIN}/inventory/default.aspx?data=editProduct&id=${productMvId}&name=${name}`;
}

export function megaventoryPurchaseOrderDocumentUrl(
  mvId: number,
  purchaseOrderNumber: string | null,
): string {
  const name = encodeURIComponent(`Purchase Order ${purchaseOrderNumber ?? ""}`);
  return `${MEGAVENTORY_ACCOUNT_WEB_ORIGIN}/inventory/default.aspx?document=PurchaseOrder&id=${mvId}&name=${name}`;
}

export function megaventorySalesOrderDocumentUrl(
  mvId: number,
  salesOrderNumber: string | null,
): string {
  const name = encodeURIComponent(`Sales Order ${salesOrderNumber ?? ""}`);
  return `${MEGAVENTORY_ACCOUNT_WEB_ORIGIN}/inventory/default.aspx?document=SalesOrder&id=${mvId}&name=${name}`;
}

/** Prefix from `public.purchase_invoice.mv_number` (for example `GIPI 1620`, `TSH 42`). */
export function megaventoryPurchaseInvoiceDocumentTemplateSlug(mvNumber: string | null): "GIPI" | "TSH" {
  const first = (mvNumber ?? "").trim().split(/\s+/)[0]?.toUpperCase();
  return first === "TSH" ? "TSH" : "GIPI";
}

function purchaseInvoiceNumberFromMvNumber(mvNumber: string | null, mvId: number): string {
  const match = /\b(?:GIPI|TSH)\s*-?\s*(\d+)\b/i.exec((mvNumber ?? "").trim());
  if (match) {
    return match[1];
  }
  return String(mvId);
}

/**
 * Megaventory document slug and list title for purchase invoice family rows.
 * GIPI → **GoodsInbound**; TSH → **HandlingInvoice** (not the custom template keys GIPI/TSH).
 */
export function megaventoryPurchaseInvoiceDocumentDisplayName(
  mvNumber: string | null,
  mvId: number,
): string {
  const numberPiece = purchaseInvoiceNumberFromMvNumber(mvNumber, mvId);
  if (megaventoryPurchaseInvoiceDocumentTemplateSlug(mvNumber) === "TSH") {
    return `Taxes Shipping and Handling ${numberPiece}`;
  }
  return `Goods In and Purchase Invoice ${numberPiece}`;
}

export function megaventoryPurchaseInvoiceDocumentSlug(mvNumber: string | null): "GoodsInbound" | "HandlingInvoice" {
  return megaventoryPurchaseInvoiceDocumentTemplateSlug(mvNumber) === "TSH" ? "HandlingInvoice" : "GoodsInbound";
}

export function megaventoryPurchaseInvoiceDocumentUrl(
  mvId: number,
  mvNumber: string | null,
): string {
  const document = megaventoryPurchaseInvoiceDocumentSlug(mvNumber);
  const name = encodeURIComponent(megaventoryPurchaseInvoiceDocumentDisplayName(mvNumber, mvId));
  return `${MEGAVENTORY_ACCOUNT_WEB_ORIGIN}/inventory/default.aspx?document=${document}&id=${mvId}&name=${name}`;
}

/**
 * Megaventory “new Taxes Shipping and Handling” form related to a GIPI.
 * Uses `pid` (parent document id), matching the HandlingInvoice create deep link.
 */
export function megaventoryNewHandlingInvoiceFromGipiUrl(gipiMvId: number): string {
  // Must match DocumentTypeDescription for DocumentTypeId 11 ("Taxes Shipping and Handling").
  const name = encodeURIComponent("New Taxes Shipping and Handling");
  return `${MEGAVENTORY_ACCOUNT_WEB_ORIGIN}/inventory/default.aspx?document=HandlingInvoice&pid=${gipiMvId}&name=${name}`;
}

export function megaventoryGoodsTransferDocumentUrl(mvId: number, mvNumber: string | null): string {
  const label = mvNumber?.trim() || String(mvId);
  const name = encodeURIComponent(`Goods Transfer ${label}`);
  return `${MEGAVENTORY_ACCOUNT_WEB_ORIGIN}/inventory/default.aspx?document=GoodsTransfer&id=${mvId}&name=${name}`;
}

/** ADJPL (−99) opens as GoodsInbound; ADJMI (−98) opens as GoodsOutbound in Megaventory. */
export function megaventoryAdjustmentDocumentUrl(mvId: number, mvNumber: string | null): string | null {
  const prefix = (mvNumber ?? "").trim().split(/\s+/)[0]?.toUpperCase() ?? "";
  let documentSlug: string;
  let namePiece: string;
  const numberPiece = mvNumber?.trim() || String(mvId);
  if (prefix === "ADJMI") {
    documentSlug = "GoodsOutbound";
    namePiece = `Adjustment Minus ${numberPiece}`;
  } else if (prefix === "ADJPL") {
    documentSlug = "GoodsInbound";
    namePiece = `Adjustment Plus ${numberPiece}`;
  } else if (prefix.startsWith("PROTO-") || prefix === "WST-") {
    documentSlug = "GoodsOutbound";
    namePiece = `Adjustment ${numberPiece}`;
  } else if (prefix.startsWith("PROTO+") || prefix === "WST+") {
    documentSlug = "GoodsInbound";
    namePiece = `Adjustment ${numberPiece}`;
  } else {
    return null;
  }
  const name = encodeURIComponent(namePiece);
  return `${MEGAVENTORY_ACCOUNT_WEB_ORIGIN}/inventory/default.aspx?document=${documentSlug}&id=${mvId}&name=${name}`;
}
